import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { CmUiFooterComponent, CmUiStandardButtonComponent } from '@cm/ui-modules';
import { SystemUpdate } from '@modules/system-updates/system-updates.model';
import { SystemUpdatesService } from '@modules/system-updates/system-updates.service';
import { SYSTEM_UPDATES_STORAGE_KEYS } from '@modules/system-updates/system-updates.config';
import { SystemUpdateBodyComponent } from '../system-update-body/system-update-body.component';
import { SystemUpdateMediaComponent } from '../system-update-media/system-update-media.component';

/**
 * Bottom-right panel with the system updates the user has not read yet. Non-modal on purpose — it
 * never dims the app; the async-task bar owns bottom-left. One update at a time, oldest first:
 *
 *  - "Next" / "Got it" records a read receipt (so it never returns, on any device) and advances.
 *  - The close button records a receipt for every update still queued.
 *  - "View all updates" opens the archive and hides the queue without a receipt; those uuids are
 *    kept in localStorage so a reload does not bring the panel back until something new arrives.
 *
 * A failed request never traps the user: news must not block the app.
 *
 * It asks again — while nothing is showing — when the tab becomes visible and on navigation, at most
 * once a minute, so an update published while the app is open turns up without a reload.
 *
 * No SHARED_IMPORTS here: it carries CmAccessModuleModule, whose TokenService provider gave AppComponent
 * (which mounts this panel) a second, empty TokenService — hasValidSession() was then always false.
 */
@Component({
  selector: 'app-system-updates-panel',
  standalone: true,
  imports: [TranslatePipe, MatIconModule, CmUiStandardButtonComponent, CmUiFooterComponent, SystemUpdateBodyComponent, SystemUpdateMediaComponent],
  templateUrl: './system-updates-panel.component.html',
  styleUrls: ['./system-updates-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SystemUpdatesPanelComponent implements OnInit {
  private readonly api = inject(SystemUpdatesService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private lastCheck = 0;

  protected readonly updates = signal<SystemUpdate[]>([]);
  protected readonly index = signal(0);
  protected readonly busy = signal(false);

  protected readonly total = computed(() => this.updates().length);
  protected readonly current = computed(() => this.updates()[this.index()] ?? null);
  protected readonly isLast = computed(() => this.index() >= this.updates().length - 1);

  ngOnInit(): void {
    this.check();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.checkAgain());
    const onVisible = () => document.visibilityState === 'visible' && this.checkAgain();
    document.addEventListener('visibilitychange', onVisible);
    this.destroyRef.onDestroy(() => document.removeEventListener('visibilitychange', onVisible));
  }

  // a later check never disturbs a queue the user is reading
  private checkAgain(): void {
    if (this.updates().length === 0 && Date.now() - this.lastCheck >= 60_000) {
      this.check();
    }
  }

  private check(): void {
    this.lastCheck = Date.now();
    this.api.getUnread().subscribe({
      next: (unread) => {
        const dismissed = this.readDismissed();
        const pending = unread.filter((update) => !dismissed.includes(update.uuid));
        // keep only uuids that are still unread, so the list cannot grow forever
        this.writeDismissed(dismissed.filter((uuid) => unread.some((update) => update.uuid === uuid)));
        this.updates.set(pending);
        this.index.set(0);
      },
      error: () => {
        /* never block the app on product news */
      },
    });
  }

  protected next(): void {
    const update = this.current();
    if (!update || this.busy()) {
      return;
    }
    this.busy.set(true);
    // advance even when the receipt fails — a stuck panel is worse than a repeated one
    this.api.markRead(update.uuid).subscribe({
      next: () => this.advance(),
      error: () => this.advance(),
    });
  }

  protected dismissAll(): void {
    for (const update of this.updates()) {
      this.api.markRead(update.uuid).subscribe({ error: () => undefined });
    }
    this.clear();
  }

  protected viewAll(): void {
    this.writeDismissed([...new Set([...this.readDismissed(), ...this.updates().map((u) => u.uuid)])]);
    this.clear();
    void this.router.navigate(['/system-updates']);
  }

  private advance(): void {
    this.busy.set(false);
    const remaining = this.updates().filter((_, position) => position !== this.index());
    this.updates.set(remaining);
    if (this.index() >= remaining.length) {
      this.index.set(Math.max(0, remaining.length - 1));
    }
  }

  private clear(): void {
    this.updates.set([]);
    this.index.set(0);
  }

  private readDismissed(): string[] {
    try {
      const parsed = JSON.parse(localStorage.getItem(SYSTEM_UPDATES_STORAGE_KEYS.PANEL_DISMISSED) ?? '[]');
      return Array.isArray(parsed) ? parsed.filter((uuid): uuid is string => typeof uuid === 'string') : [];
    } catch {
      return [];
    }
  }

  private writeDismissed(uuids: string[]): void {
    try {
      localStorage.setItem(SYSTEM_UPDATES_STORAGE_KEYS.PANEL_DISMISSED, JSON.stringify(uuids));
    } catch {
      /* storage unavailable — the panel just shows again next time */
    }
  }
}
